'use client'

import { useEffect, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// A page's numbered module (the design system's .ops-widget): a foreground
// top rule, a heading row -- "01" in the signal colour, a chevron, the icon,
// the title at 16 px, a mono summary, actions on the right -- and a body
// under a hairline. The title folds the body; with a storageKey the fold
// is remembered. ModuleStack numbers its Modules with a CSS counter, so a
// module needs no index prop and a conditional one keeps the sequence.
//
// `compact` is the same grammar at panel size (13 px title, no counter):
// detail-page widgets and side panels.

export function ModuleStack({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`flex flex-col gap-10 [counter-reset:module] ${className}`}>{children}</div>
}

export function Module({
  title, icon: Icon, summary, actions, children,
  collapsed, onToggle, defaultCollapsed = false, storageKey,
  compact = false, tone, className = '', bodyClassName = '', id,
}: {
  title: ReactNode
  icon?: LucideIcon
  summary?: ReactNode
  actions?: ReactNode
  children?: ReactNode
  /** Controlled fold. */
  collapsed?: boolean
  onToggle?: () => void
  /** Uncontrolled fold's initial state; storageKey remembers the choice. */
  defaultCollapsed?: boolean
  storageKey?: string
  compact?: boolean
  /** A coloured rule on the module's left edge (a detail panel's accent). */
  tone?: 'signal' | 'success' | 'warning' | 'danger'
  className?: string
  bodyClassName?: string
  id?: string
}) {
  const controlled = collapsed !== undefined
  const [own, setOwn] = useState<boolean>(() => {
    if (storageKey && typeof window !== 'undefined') {
      try {
        const v = window.localStorage.getItem(`tf-module:${storageKey}`)
        if (v === '1' || v === '0') return v === '1'
      } catch { /* no storage */ }
    }
    return defaultCollapsed
  })
  const folded = controlled ? !!collapsed : own
  useEffect(() => {
    if (!storageKey || controlled) return
    try { window.localStorage.setItem(`tf-module:${storageKey}`, own ? '1' : '0') } catch { /* no storage */ }
  }, [own, storageKey, controlled])
  const toggle = () => (controlled ? onToggle?.() : setOwn((v) => !v))

  const toneRule = tone ? { signal: 'border-l-signal', success: 'border-l-success', warning: 'border-l-warning', danger: 'border-l-destructive' }[tone] : ''

  return (
    <section
      id={id}
      className={`min-w-0 [counter-increment:module] ${compact ? `border border-border bg-card ${tone ? `border-l-2 ${toneRule}` : ''}` : 'border-t border-foreground'} ${className}`}
    >
      <div className={`flex items-start justify-between gap-3 ${compact ? 'min-h-[40px] px-4 py-2' : 'min-h-[58px] py-3'}`}>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!folded}
          className={`flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-left transition-colors hover:text-signal ${compact ? 'text-body font-medium' : 'text-title'}`}
        >
          {!compact && (
            <span className="pt-1 font-mono text-label text-signal before:content-[counter(module,decimal-leading-zero)]" aria-hidden />
          )}
          <ChevronDown className={`h-[14px] w-[14px] shrink-0 text-signal transition-transform ${folded ? '-rotate-90' : ''}`} />
          {Icon && <Icon className={`${compact ? 'h-3.5 w-3.5' : 'h-[15px] w-[15px]'} shrink-0 text-muted-foreground`} strokeWidth={1.6} />}
          <span className="min-w-0 text-foreground">{title}</span>
          {summary && <span className="pt-[2px] font-mono text-label font-normal text-muted-foreground">{summary}</span>}
        </button>
        {actions && <div className="flex shrink-0 items-center gap-1.5 pt-1">{actions}</div>}
      </div>
      {!folded && children !== undefined && (
        <div className={`border-t border-border ${bodyClassName}`}>{children}</div>
      )}
    </section>
  )
}

/** The one-line mono footer under a module's list ("12 schedules · 5 enabled · …"). */
export function ModuleFooter({ children }: { children: ReactNode }) {
  return <div className="border-t border-border px-5 py-3 font-mono text-micro leading-relaxed text-muted-foreground">{children}</div>
}
